import { cn } from '@/lib/utils';
import { useMemo, useState } from 'react';

import type { IStep } from '@chainlit/react-client';
import { Check, ChevronRight, Loader2, TriangleAlert } from 'lucide-react';

interface Props {
  steps: IStep[];
}

const isStepRunning = (step: IStep) =>
  Boolean(step.start) && !step.end && !step.isError;

const formatDuration = (step: IStep): string | undefined => {
  const start =
    typeof step.start === 'number' ? step.start : Date.parse(String(step.start));
  const end = typeof step.end === 'number' ? step.end : Date.parse(String(step.end));
  if (!Number.isFinite(start) || !Number.isFinite(end)) return undefined;
  const ms = Math.max(0, end - start);
  return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;
};

const badgeOf = (step: IStep) => {
  if (isStepRunning(step)) return { text: '进行中', cls: 'bg-blue-500/10 text-blue-600 dark:text-blue-400' };
  if (step.isError) return { text: '失败', cls: 'bg-red-500/10 text-red-600 dark:text-red-400' };
  return { text: '已完成', cls: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' };
};

const StepIcon = ({ step }: { step: IStep }) => {
  if (isStepRunning(step))
    return <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-blue-500" />;
  if (step.isError) return <TriangleAlert className="h-3.5 w-3.5 shrink-0 text-red-500" />;
  return <Check className="h-3.5 w-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" />;
};

const ToolRow = ({ step }: { step: IStep }) => {
  const badge = badgeOf(step);
  const duration = formatDuration(step);
  return (
    <div className="flex items-center gap-2.5 py-[5px]">
      <StepIcon step={step} />
      <span className="truncate font-mono text-xs font-semibold text-foreground">
        {step.name}
      </span>
      {duration ? (
        <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">
          {duration}
        </span>
      ) : null}
      <span className="grow" />
      <span
        className={cn(
          'shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium',
          badge.cls
        )}
      >
        {badge.text}
      </span>
    </div>
  );
};

/**
 * 同一层级连续工具步骤的聚合卡：收起时仅占一行（正在处理… / 已完成 · N 步），
 * 展开后逐工具行 + 开发者详情（调用参数 / 执行结果）。
 * 工具产出的图片元素不在本组件内 —— 它们附着在独立的 assistant 消息上，渲染在本卡之外。
 */
export default function ToolWorkflow({ steps }: Props) {
  const [open, setOpen] = useState(false);

  const runningStep = useMemo(() => steps.find(isStepRunning), [steps]);
  const anyError = useMemo(() => steps.some((s) => s.isError), [steps]);
  const hasDetails = useMemo(
    () => steps.some((s) => s.input || s.output),
    [steps]
  );
  const running = Boolean(runningStep);

  return (
    <div className="mb-2 w-full">
      <div className="rounded-lg border border-border bg-muted/40 text-[13px] leading-normal">
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className="flex w-full items-center gap-2.5 rounded-lg px-3.5 py-2.5 text-left hover:bg-muted/60"
        >
          {running ? (
            <Loader2 className="h-4 w-4 shrink-0 animate-spin text-blue-500" />
          ) : anyError ? (
            <TriangleAlert className="h-4 w-4 shrink-0 text-red-500" />
          ) : (
            <Check className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
          )}
          <span
            className={cn(
              'min-w-0 grow truncate',
              running ? 'loading-shimmer text-muted-foreground' : 'text-muted-foreground'
            )}
          >
            {running
              ? `正在处理…${runningStep ? ` · ${runningStep.name}` : ''}`
              : `处理过程已完成 · ${steps.length} 步`}
          </span>
          <span className="hidden shrink-0 text-xs text-muted-foreground sm:inline">
            处理过程
          </span>
          <ChevronRight
            className={cn(
              'h-4 w-4 shrink-0 text-muted-foreground transition-transform',
              open && 'rotate-90'
            )}
          />
        </button>
        {open ? (
          <div className="border-t border-border px-3.5 pb-3 pt-1.5">
            {steps.map((step) => (
              <ToolRow key={step.id} step={step} />
            ))}
            {hasDetails ? (
              <details className="mt-2">
                <summary className="cursor-pointer select-none text-xs text-muted-foreground">
                  开发者详情
                </summary>
                <div className="mt-2 space-y-3">
                  {steps.map((step) => {
                    const duration = formatDuration(step);
                    return (
                      <div key={step.id} className="space-y-1.5">
                        <div className="font-mono text-[11px] font-semibold text-muted-foreground">
                          {step.name}
                          {duration ? ` · ${duration}` : ''}
                          {step.isError ? ' · 失败' : ''}
                        </div>
                        {step.input ? (
                          <pre className="max-h-40 overflow-auto whitespace-pre-wrap break-all rounded-md bg-zinc-900 p-2.5 font-mono text-[11px] leading-relaxed text-zinc-200">
                            {step.input}
                          </pre>
                        ) : null}
                        {step.output ? (
                          <pre className="max-h-60 overflow-auto whitespace-pre-wrap break-all rounded-md bg-zinc-900 p-2.5 font-mono text-[11px] leading-relaxed text-zinc-200">
                            {step.output}
                          </pre>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </details>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
